import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import RepresentOathHearingsCardsList from './components/RepresentOathHearingsCardsList';
import RepresentationConfirmation from './components/RepresentationConfirmation';
import Spinner from '../../components/Spinner';
import {
	fetchItems,
	submitRepresentation,
	resetState,
} from '../../../data/reducers/representOathHearingsReducer';
import './RepresentOathHearingsPage.css';

/**
 * OATH hearing representation selection page.
 *
 * Reached from the "Represent for Me" button in the biweekly OATH Hearing
 * Outreach email. Allows users to:
 * - View the upcoming hearings the outreach email listed
 * - Select which ones they would like representation for
 * - Submit a representation request to our team
 *
 * URL Parameters (signed by the backend; sent on unchanged with both API calls):
 * - user_id: User ID
 * - email_ids: Comma-separated queued email IDs
 * - expires, signature: The link's signature
 */
const RepresentOathHearingsPage = () => {
	const dispatch = useDispatch();
	const history = useHistory();

	// Get URL parameters
	const signedQuery = window.location.search;
	const urlParams = new URLSearchParams(signedQuery);
	const hasSignedLink =
		!!urlParams.get('user_id') &&
		!!urlParams.get('email_ids') &&
		!!urlParams.get('signature');

	const {
		items,
		selectedItems,
		isLoading,
		isSubmitting,
		error,
		submitSuccess,
	} = useSelector((state) => state.representOathHearings);

	const [showConfirmation, setShowConfirmation] = useState(false);

	useEffect(() => {
		// Reset state on mount
		dispatch(resetState());

		// Validate parameters
		if (!hasSignedLink) {
			return;
		}

		// Fetch items
		dispatch(fetchItems(signedQuery));
	}, [hasSignedLink, signedQuery, dispatch]);

	/**
	 * Handle submission of selected hearings.
	 */
	const handleSubmit = () => {
		if (selectedItems.length === 0) {
			alert('Please select at least one hearing to represent.');
			return;
		}

		dispatch(submitRepresentation(signedQuery, selectedItems));
	};

	// Show confirmation page on success
	useEffect(() => {
		if (submitSuccess) {
			setShowConfirmation(true);
		}
	}, [submitSuccess]);

	const navigate = useCallback(
		(path) => {
			history.push(path);
		},
		[history],
	);

	if (showConfirmation && submitSuccess) {
		return <RepresentationConfirmation itemCount={selectedItems.length} />;
	}

	return (
		<div className="represent-oath-hearings-page">
			<div className="represent-oath-hearings-container">
				<header className="represent-oath-hearings-header">
					<h1>Represent for Me</h1>
					<p>Select the upcoming OATH/ECB hearings you'd like our team to represent you at</p>
				</header>

				{isLoading && (
					<div className="represent-oath-hearings-loading">
						<Spinner />
						<p>Loading hearings...</p>
					</div>
				)}

				{error && (
					<div className="represent-oath-hearings-error">
						<h3>Error</h3>
						<p>{error}</p>
						<button
							onClick={() => navigate('/dashboard')}
							className="btn btn-primary"
						>
							Go to Dashboard
						</button>
					</div>
				)}

				{!isLoading && !error && items.length === 0 && !hasSignedLink && (
					<div className="represent-oath-hearings-error">
						<h3>Invalid Request</h3>
						<p>This link is incomplete. Please use the Represent for Me button in your OATH hearings email.</p>
						<button
							onClick={() => navigate('/dashboard')}
							className="btn btn-primary"
						>
							Go to Dashboard
						</button>
					</div>
				)}

				{!isLoading && !error && items.length === 0 && hasSignedLink && (
					<div className="represent-oath-hearings-empty">
						<h3>No Hearings Found</h3>
						<p>There are no hearings available for representation.</p>
						<button
							onClick={() => navigate('/dashboard')}
							className="btn btn-primary"
						>
							Go to Dashboard
						</button>
					</div>
				)}

				{!isLoading && !error && items.length > 0 && (
					<>
						<div className="represent-oath-hearings-top-action">
							<button
								className="btn btn-primary"
								onClick={handleSubmit}
								disabled={selectedItems.length === 0 || isSubmitting}
							>
								{isSubmitting
									? 'Submitting...'
									: `Request Representation for ${selectedItems.length} Hearing(s)`}
							</button>
						</div>

						<RepresentOathHearingsCardsList
							items={items}
							selectedItems={selectedItems}
						/>

						<div className="represent-oath-hearings-footer">
							<div className="selection-summary">
								<strong>{selectedItems.length}</strong> hearing(s) selected
							</div>
							<div className="action-buttons">
								<button
									className="btn btn-secondary"
									onClick={() => navigate('/dashboard')}
									disabled={isSubmitting}
								>
									Cancel
								</button>
								<button
									className="btn btn-primary"
									onClick={handleSubmit}
									disabled={selectedItems.length === 0 || isSubmitting}
								>
									{isSubmitting
										? 'Submitting...'
										: `Request Representation for ${selectedItems.length} Hearing(s)`}
								</button>
							</div>
						</div>
					</>
				)}
			</div>
		</div>
	);
};

export default RepresentOathHearingsPage;
